import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import Pagination from '../../../../components/Pagination';
import { appDate } from '../../../../utils/appDateFormat';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../../../../components/ui/Dialog';
import {
  getTemplates,
  deleteTemplate,
  duplicateTemplate,
  archiveTemplate,
  restoreTemplate,
  publishTemplate,
  unpublishTemplate,
  getDownloadHistory,
  DocumentTemplateCategory,
  DocumentTemplateStatus,
} from '../../../../api/General/documentTemplates';

const CATEGORY_OPTIONS = [
  { value: DocumentTemplateCategory.Contract, label: 'Contract' },
  { value: DocumentTemplateCategory.Certificate, label: 'Certificate' },
  { value: DocumentTemplateCategory.Letter, label: 'Letter' },
  { value: DocumentTemplateCategory.Settlement, label: 'Settlement' },
  { value: DocumentTemplateCategory.Other, label: 'Other' },
];

const STATUS_BADGE = {
  [DocumentTemplateStatus.Draft]: { label: 'Draft', cls: 'bg-amber-100 text-amber-800' },
  [DocumentTemplateStatus.Published]: { label: 'Published', cls: 'bg-green-100 text-green-800' },
  [DocumentTemplateStatus.Archived]: { label: 'Archived', cls: 'bg-muted text-muted-foreground' },
};

const PAGE_SIZE = 12;

const TemplateDirectory = ({ onEdit, onCreateNew, showToast, reloadNonce }) => {
  const { t, i18n } = useTranslation();

  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [localNonce, setLocalNonce] = useState(0);

  // Delete dialog
  const [deleting, setDeleting] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  // Download-history dialog
  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Per-row busy (publish/duplicate/archive/restore)
  const [rowBusyId, setRowBusyId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getTemplates({
        search: debouncedSearch || undefined,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        page,
        pageSize: PAGE_SIZE,
      });
      if (result.errors.length === 0) {
        setItems(result.payload.items);
        setTotalPages(result.payload.totalPages);
        setTotalCount(result.payload.totalCount);
      } else {
        showToast(result.errors[0]?.description || t('Something went wrong'), 'error');
      }
    } catch (err) {
      showToast(err?.errors?.[0]?.description || t('Something went wrong'), 'error');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, categoryFilter, statusFilter, page, showToast, t]);

  useEffect(() => {
    load();
  }, [load, reloadNonce, localNonce]);

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return appDate(value) || '—';
    } catch {
      return '—';
    }
  };

  const categoryLabel = (category) => t(category);

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      const result = await deleteTemplate(deleting.id);
      if (result.errors.length === 0) {
        showToast(t('Template deleted'), 'success');
        setDeleting(null);
        setLocalNonce((n) => n + 1);
      } else {
        showToast(result.errors[0]?.description || t('Something went wrong'), 'error');
      }
    } catch (err) {
      showToast(err?.errors?.[0]?.description || t('Something went wrong'), 'error');
    } finally {
      setDeletingBusy(false);
    }
  };

  const handleDuplicate = async (template) => {
    setRowBusyId(template.id);
    try {
      const result = await duplicateTemplate(template.id);
      if (result.errors.length === 0) {
        showToast(t('Template duplicated'), 'success');
        setLocalNonce((n) => n + 1);
      } else {
        showToast(result.errors[0]?.description || t('Something went wrong'), 'error');
      }
    } catch (err) {
      showToast(err?.errors?.[0]?.description || t('Something went wrong'), 'error');
    } finally {
      setRowBusyId(null);
    }
  };

  const handleTogglePublish = async (template) => {
    const isPublished = template.status === DocumentTemplateStatus.Published;
    setRowBusyId(template.id);
    try {
      const result = isPublished ? await unpublishTemplate(template.id) : await publishTemplate(template.id);
      if (result.errors.length === 0) {
        showToast(isPublished ? t('Template unpublished') : t('Template published'), 'success');
        setLocalNonce((n) => n + 1);
      } else {
        showToast(result.errors[0]?.description || t('Something went wrong'), 'error');
      }
    } catch (err) {
      showToast(err?.errors?.[0]?.description || t('Something went wrong'), 'error');
    } finally {
      setRowBusyId(null);
    }
  };

  const handleToggleArchive = async (template) => {
    const isArchived = template.status === DocumentTemplateStatus.Archived;
    setRowBusyId(template.id);
    try {
      const result = isArchived ? await restoreTemplate(template.id) : await archiveTemplate(template.id);
      if (result.errors.length === 0) {
        showToast(isArchived ? t('Template restored') : t('Template archived'), 'success');
        setLocalNonce((n) => n + 1);
      } else {
        showToast(result.errors[0]?.description || t('Something went wrong'), 'error');
      }
    } catch (err) {
      showToast(err?.errors?.[0]?.description || t('Something went wrong'), 'error');
    } finally {
      setRowBusyId(null);
    }
  };

  const openHistory = async (template) => {
    setHistoryOf(template);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const result = await getDownloadHistory(template.id);
      if (result.errors.length === 0) setHistory(result.payload || []);
      else showToast(result.errors[0]?.description || t('Something went wrong'), 'error');
    } catch (err) {
      showToast(err?.errors?.[0]?.description || t('Something went wrong'), 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const hasActiveFilters = debouncedSearch || categoryFilter !== 'all' || statusFilter !== 'all';

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          placeholder={t('Search templates by name or file')}
          value={search}
          onChange={(e) => setSearch(e?.target?.value)}
        />
        <Select
          value={categoryFilter}
          onChange={(value) => {
            setCategoryFilter(value);
            setPage(1);
          }}
          options={[{ value: 'all', label: t('All Categories') }, ...CATEGORY_OPTIONS.map((c) => ({ value: c.value, label: t(c.label) }))]}
        />
        <Select
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          options={[
            { value: 'all', label: t('All Statuses') },
            { value: DocumentTemplateStatus.Draft, label: t('Draft') },
            { value: DocumentTemplateStatus.Published, label: t('Published') },
            { value: DocumentTemplateStatus.Archived, label: t('Archived') },
          ]}
        />
      </div>

      {/* Body */}
      {loading ? (
        <div className="bg-card rounded-lg border border-border p-12 text-center text-muted-foreground">
          {t('Loading templates...')}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <div className="w-14 h-14 bg-muted rounded-xl flex items-center justify-center mx-auto mb-4">
            <Icon name="FileText" size={28} className="text-muted-foreground" />
          </div>
          <p className="text-foreground text-lg font-medium">
            {hasActiveFilters ? t('No templates match your filters') : t('No templates yet')}
          </p>
          <p className="text-muted-foreground text-sm mt-1 mb-5">
            {hasActiveFilters
              ? t('Try adjusting your search or filters.')
              : t('Upload a Word template so employees can download personalized documents.')}
          </p>
          {!hasActiveFilters && (
            <Button onClick={onCreateNew} iconName="Plus" iconPosition="left">
              {t('Create Template')}
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Template Name')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Category')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Fields')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Last Used')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Downloads')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Status')}</th>
                  <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase">{t('Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((template) => {
                  const isArchived = template.status === DocumentTemplateStatus.Archived;
                  const isPublished = template.status === DocumentTemplateStatus.Published;
                  const badge = STATUS_BADGE[template.status] || STATUS_BADGE[DocumentTemplateStatus.Draft];
                  const busy = rowBusyId === template.id;
                  // Mirror the server publish-guard client-side: every detected placeholder
                  // must be mapped to a field, else publishing is blocked.
                  const unmappedCount = (template.detectedFields ?? []).filter(
                    (f) => !(template.fieldMappings ?? []).some((m) => m.placeholder === f && m.mappedTo)
                  ).length;
                  const canPublish = unmappedCount === 0;
                  return (
                    <tr key={template.id} className="hover:bg-muted/30">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="FileText" size={18} className="text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{template.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{template.originalFileName}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{categoryLabel(template.category)}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{template.detectedFields?.length ?? 0}</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(template.lastUsedOn)}</td>
                      <td className="px-6 py-4 text-sm font-medium text-foreground">{template.timesGenerated}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.cls}`}>{t(badge.label)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="SquarePen"
                            iconPosition="left"
                            disabled={busy}
                            onClick={() => onEdit(template)}
                          >
                            {t('Edit')}
                          </Button>
                          {!isArchived && (
                            <Button
                              variant="ghost"
                              size="icon"
                              loading={busy}
                              disabled={busy || (!isPublished && !canPublish)}
                              onClick={() => handleTogglePublish(template)}
                              title={
                                isPublished
                                  ? t('Unpublish')
                                  : canPublish
                                  ? t('Publish')
                                  : t('Map all placeholders before publishing')
                              }
                            >
                              <Icon name={isPublished ? 'EyeOff' : 'Send'} size={16} className={isPublished ? '' : (canPublish ? 'text-green-600' : '')} />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" disabled={busy} onClick={() => openHistory(template)} title={t('Download history')}>
                            <Icon name="History" size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" loading={busy} onClick={() => handleDuplicate(template)} title={t('Duplicate')}>
                            <Icon name="Copy" size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            loading={busy}
                            onClick={() => handleToggleArchive(template)}
                            title={isArchived ? t('Restore') : t('Archive')}
                          >
                            <Icon name={isArchived ? 'ArchiveRestore' : 'Archive'} size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" disabled={busy} onClick={() => setDeleting(template)} title={t('Delete')}>
                            <Icon name="Trash2" size={16} className="text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} totalCount={totalCount} />
      )}

      {/* Download-history dialog */}
      <Dialog open={!!historyOf} onOpenChange={(open) => !open && setHistoryOf(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Download history')}</DialogTitle>
            <DialogDescription>
              {historyOf ? historyOf.name : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {historyLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">{t('Loading...')}</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">{t('No downloads yet')}</p>
            ) : (
              <ul className="divide-y divide-border max-h-72 overflow-y-auto">
                {history.map((row, idx) => (
                  <li key={idx} className="flex items-center justify-between py-2.5">
                    <span className="text-sm text-foreground">{row.userName || t('Unknown user')}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(row.downloadedOn)}</span>
                  </li>
                ))}
              </ul>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOf(null)}>
              {t('Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleting} onOpenChange={(open) => !open && !deletingBusy && setDeleting(null)}>
        <DialogContent
          onEscapeKeyDown={(e) => deletingBusy && e.preventDefault()}
          onPointerDownOutside={(e) => deletingBusy && e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{t('Delete Template')}</DialogTitle>
            <DialogDescription>
              {t('This permanently removes the template and its file. This action cannot be undone.')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground">
              {t('Delete')} <span className="font-semibold">{deleting?.name}</span>?
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={deletingBusy}>
              {t('Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} loading={deletingBusy}>
              {t('Delete template')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateDirectory;
